import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Sparkles, UserRound, Trees, Ruler, FolderOpen, Users, CreditCard, Receipt, Settings as Cog, LayoutGrid, Shield, LogOut, Menu, MailWarning } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo, LangSwitch } from "@/components/common";

const GROUPS = [
  ["", [["overview", "/app", LayoutGrid]]],
  ["groupTools", [["hero", "/app/hero", Sparkles], ["model", "/app/model", UserRound], ["lifestyle", "/app/lifestyle", Trees], ["dimensions", "/app/dimensions", Ruler]]],
  ["groupLibrary", [["projects", "/app/projects", FolderOpen], ["models", "/app/models", Users]]],
  ["groupAccount", [["subscription", "/app/subscription", CreditCard], ["billing", "/app/billing", Receipt], ["settings", "/app/settings", Cog]]],
];

export function UsageMeter({ ent }) {
  const { t } = useI18n();
  if (!ent) return null;
  const pct = ent.active ? Math.min(100, (ent.used / Math.max(1, ent.quota)) * 100) : 0;
  return (
    <div className="panel p-3 text-xs" data-testid="usage-meter">
      <p className="text-neutral-400 mb-1.5">{t("app.usage")}</p>
      {ent.active ? (
        <>
          <p className="font-semibold" data-testid="usage-quota-text">{t("app.quotaLeft", { used: ent.used, quota: ent.quota })}</p>
          <div className="h-1.5 rounded-full bg-[var(--bg-2)] mt-2 overflow-hidden"><div className="h-full bg-[var(--brand)]" style={{ width: `${pct}%` }} /></div>
        </>
      ) : (
        <p className="font-semibold" data-testid="usage-trial-text">{t("app.trialLeft", { n: ent.trial_remaining })}</p>
      )}
    </div>
  );
}

function SideNav({ ent, onNavigate }) {
  const { t } = useI18n();
  const { user, logout, refreshMe } = useAuth();
  const nav = useNavigate();
  const changeLang = (l) => api.patch("/auth/profile", { lang: l }).then(refreshMe).catch(() => {});
  return (
    <div className="flex flex-col h-full p-4 gap-5">
      <div className="flex items-center justify-between"><Logo to="/app" /></div>
      <nav className="flex-1 overflow-y-auto space-y-5">
        {GROUPS.map(([g, items]) => (
          <div key={g || "root"}>
            {g && <p className="px-3 mb-1.5 text-[11px] uppercase tracking-[0.16em] text-neutral-500">{t(`app.${g}`)}</p>}
            {items.map(([k, to, Icon]) => (
              <NavLink key={k} to={to} end={to === "/app"} onClick={onNavigate} data-testid={`sidenav-${k}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-[var(--bg-2)] text-white" : "text-neutral-400 hover:text-white hover:bg-[var(--bg-2)]/60"}`}>
                <Icon size={17} />{t(`app.nav.${k}`)}
              </NavLink>
            ))}
          </div>
        ))}
        {user?.role === "admin" && (
          <NavLink to="/admin" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-amber-300 hover:bg-[var(--bg-2)]" data-testid="sidenav-admin">
            <Shield size={17} />{t("app.nav.admin")}
          </NavLink>
        )}
      </nav>
      <UsageMeter ent={ent} />
      <div className="flex items-center justify-between">
        <LangSwitch onChange={changeLang} />
        <button onClick={async () => { await logout(); nav("/"); }} className="text-neutral-400 hover:text-white p-2" data-testid="logout-btn" aria-label={t("common.signOut")}>
          <LogOut size={18} />
        </button>
      </div>
    </div>
  );
}

function VerifyBanner() {
  const { t, errMsg } = useI18n();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!user || user.email_verified) return null;
  const resend = async () => {
    setBusy(true);
    try { await api.post("/auth/resend-verification"); toast.success(t("auth.resent")); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mb-6 panel border-amber-700/60 px-4 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid="verify-email-banner">
      <MailWarning size={18} className="text-amber-300 shrink-0" />
      <span className="flex-1 text-neutral-200">{user.verification_sent_at ? t("auth.verifyBanner", { email: user.email }) : t("auth.verifyBannerNoSend")}</span>
      <button onClick={resend} disabled={busy} className="text-amber-300 hover:underline disabled:opacity-50" data-testid="resend-verification-btn">{user.verification_sent_at ? t("auth.resend") : t("auth.sendLink2")}</button>
    </div>
  );
}

export default function AppLayout() {
  const [config, setConfig] = useState(null);
  const [ent, setEnt] = useState(null);
  const [open, setOpen] = useState(false);
  const reloadEnt = useCallback(() => api.get("/billing/status").then((r) => setEnt(r.data)).catch(() => {}), []);
  useEffect(() => {
    api.get("/public/config").then((r) => setConfig(r.data));
    reloadEnt();
  }, [reloadEnt]);
  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[260px_1fr]">
      <aside className="hidden lg:block sticky top-0 h-screen border-r border-[var(--line)] bg-[var(--bg-1)]"><SideNav ent={ent} /></aside>
      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 border-b border-[var(--line)] bg-[#121212]/90 backdrop-blur">
        <Logo to="/app" />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild><button className="p-2" data-testid="mobile-menu-btn"><Menu /></button></SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 bg-[var(--bg-1)]"><SideNav ent={ent} onNavigate={() => setOpen(false)} /></SheetContent>
        </Sheet>
      </div>
      <main className="px-4 sm:px-8 py-8 max-w-[1400px] w-full mx-auto">
        <VerifyBanner />
        {config ? <Outlet context={{ config, ent, reloadEnt }} /> : <p className="text-neutral-500">…</p>}
      </main>
    </div>
  );
}
